import { CompositeNavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import { StackHeaderProps, StackNavigationProp } from '@react-navigation/stack';
import React, { useCallback, useLayoutEffect } from 'react';

import { HeaderTextButton } from '~/components/header/header-text-button';
import { HelttiHeaderStackHeaderWrapper } from '~/components/heltti-header';
import { Loader } from '~/components/loader';
import { ScreenContainer } from '~/components/screen';
import { AppointmentsSelectionProvider } from '~/contexts/appointments-selection';
import { useIntl } from '~/contexts/intl';
import { useRegions } from '~/hooks/regions';
import { useRoute } from '~/hooks/route/route';
import { AppNavigatorParamList } from '~/navigator/app-navigator';
import { MainNavigatorParamList } from '~/navigator/main-navigator';

import { ViewAppointmentsContent } from './view-appointments-content';

export type AppointmentsNavigationProp = CompositeNavigationProp<
    StackNavigationProp<MainNavigatorParamList, 'appointments'>,
    StackNavigationProp<AppNavigatorParamList>
>;

export type AppointmentsRouteProp = RouteProp<MainNavigatorParamList, 'appointments'>;

export const Appointments: React.FC = () => {
    const { params } = useRoute<AppointmentsRouteProp>();
    const { canGoBack, goBack, navigate, setOptions } = useNavigation<AppointmentsNavigationProp>();
    const { formatMessage } = useIntl();

    const { regions, loading } = useRegions();
    const initialRegion = params.remote
        ? undefined
        : params.regionId
        ? regions?.find(region => region.id === params.regionId)
        : regions?.[0];

    const handleAbort = useCallback(() => {
        if (canGoBack()) {
            goBack();
        } else {
            navigate('profile');
        }
    }, [canGoBack, goBack, navigate]);

    useLayoutEffect(() => {
        setOptions({
            header: (props: StackHeaderProps) => (
                <HelttiHeaderStackHeaderWrapper
                    {...props}
                    headerRight={<HeaderTextButton onPress={handleAbort} label={formatMessage('appointments.abort')} />}
                />
            )
        });
    }, [formatMessage, handleAbort, setOptions]);

    return (
        <ScreenContainer>
            {loading ? (
                <Loader size="large" />
            ) : (
                <AppointmentsSelectionProvider
                    calendarReservationAccessId={params.calendarReservationAccessId}
                    calendarEventId={params.calendarEventId}
                    initialAppointmentDaysRange={params.appointmentDaysRange}
                    initialDate={params.date}
                    initialTimeOfDay={params.timeOfDay}
                    region={initialRegion}
                    initialFilters={params.filters}
                >
                    <ViewAppointmentsContent />
                </AppointmentsSelectionProvider>
            )}
        </ScreenContainer>
    );
};
